import SortArrows from '@rio-cloud/rio-uikit/SortArrows';
import classNames from 'classnames';
import { useMemo } from 'react';

import type { ProductType } from '../../common/product/product';
import type { TermedLevel } from '../../common/termedLevel/types';
import { isManNowProduct } from '../../config/manNowProducts';
import { AllContractsCheckboxContainer } from './AllContractsCheckboxContainer';
import { ContractTableRowContainer, getColumns } from './ContractTableRowContainer';
import { useSorting } from './useSorting';

const tableClassNames = () =>
    classNames(
        'table',
        'table-layout-fixed',
        'table-column-overflow-hidden',
        'table-condensed',
        'table-sticky',
        'table-sticky-in-container',
        'table-hover',
        'table-head-filled'
    );

interface Props {
    productId: string;
    serviceName: string;
    contracts: TermedLevel[];
    resourceType: ProductType;
}

export const ContractTable = (props: Props) => {
    const { productId, contracts, resourceType, serviceName } = props;
    const { sort, handleSortChange, sortDirectionForProperty } = useSorting<TermedLevel>('resourceName');

    const sortedContracts = sort(contracts);

    const columns = useMemo(() => getColumns(resourceType), []);

    return (
        <>
            <hr className={'margin-0'} />
            <div
                className={'max-height-50vh width-100pct overflow-y-auto border border-bottom-only'}
                data-onboarding-contracts-walkthrough-step='contract-view-table'
            >
                <table className={tableClassNames()}>
                    <colgroup>
                        <col className='table-checkbox' />
                        {columns.map(column => (
                            <col key={column.id} style={{ width: column?.width }} />
                        ))}
                    </colgroup>
                    <thead>
                        <tr>
                            <th>
                                <span>
                                    {!isManNowProduct(productId) && (
                                        <AllContractsCheckboxContainer productId={productId} />
                                    )}
                                </span>
                            </th>
                            {columns.map(column => (
                                <th
                                    key={column.id}
                                    className={classNames({
                                        'sort-column': column.propertyName,
                                        'text-right': column.id === 'actions',
                                    })}
                                    onClick={
                                        column.propertyName
                                            ? () => handleSortChange(column.propertyName as keyof TermedLevel)
                                            : undefined
                                    }
                                >
                                    <span>
                                        {column.propertyName && (
                                            <SortArrows direction={sortDirectionForProperty(column.propertyName)} />
                                        )}
                                        {column.label}
                                    </span>
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {sortedContracts.map(contract => (
                            <ContractTableRowContainer
                                key={contract.productId + contract.resourceId}
                                contract={contract}
                                serviceName={serviceName}
                            />
                        ))}
                    </tbody>
                </table>
            </div>
        </>
    );
};
